import React, { useState, useCallback, useContext, useMemo } from 'react'
import styles from './MonthlyCostPanel.module.scss'
import PropTypes from 'prop-types'

// Analytics
import { useLogGAEvent } from '../../../../hooks/googleAnalytics'

// Context
import { AppContext } from '../../../../contexts'

// Constants
import { EDIT_ESTIMATED_COSTS_TEASER_CLICK } from '../../../../constants/googleAnalytics'
import { INPUT_NAMES } from '../../../../constants/display'
import { DEFAULT_DOWN_PAYMENT } from '../../../../constants/buyerSettings'

// Helpers
import { formatToCurrency, formatToPercent } from '../../../../helpers'

// Components
import {
  If,
  OverlayPanel,
  OverlayPanelHeader,
  Panel,
  Text,
  PanelDivider,
  Button,
  NavigationButton,
  UnderConstructionModal,
  ColoredCountUp,
} from '../../../../components'
import { HorizontalStackedBarChart } from '../../../../components/Charts'

// Hooks
import { useListingDisplay } from '../../../../hooks/listing'
import {
  useBuyerSettingsWithDefaults,
  useBuyerSettings,
} from '../../../../hooks/buyerSettings'

// SVGs
import { BudgetRightArrow } from '../../../../components/Svg'

const MonthlyCostPanel = ({ handleBack }) => {
  const {
    listingPrice,
    monthlyCost,
    monthlyCostBreakdown = {},
    mostRecentInterestRate,
  } = useListingDisplay()

  const { loanPayment } = monthlyCostBreakdown

  const { downPayment } = useBuyerSettings()
  const { maxBudget } = useBuyerSettingsWithDefaults()
  const { setFocusFieldAndNavigateToSettings } = useContext(AppContext)
  const budgetDiff = monthlyCost - maxBudget
  const budgetDirection = budgetDiff > 0 ? 'Over' : 'Under'
  const budgetColor = budgetDirection === 'Over' ? 'red' : 'green'
  const formattedBudgetDiff = formatToCurrency(Math.abs(budgetDiff))

  const [underConstructionActive, setUnderConstructionActive] = useState(false)
  const logGA = useLogGAEvent(EDIT_ESTIMATED_COSTS_TEASER_CLICK)

  const toggleUnderConstructionActive = useCallback(
    _ => {
      if (!underConstructionActive) {
        logGA()
      }
      setUnderConstructionActive(!underConstructionActive)
    },
    [underConstructionActive, logGA]
  )

  const DownPaymentSubtitle = () => {
    const handleNavigation = () => {
      handleBack()
      setFocusFieldAndNavigateToSettings(INPUT_NAMES.DOWN_PAYMENT)
    }
    // TODO: can delete default downpayment here after 1836 goes in, moving this logic to the BE
    return (
      <>
        <NavigationButton onClick={handleNavigation}>
          {downPayment
            ? formatToCurrency(downPayment)
            : formatToCurrency(DEFAULT_DOWN_PAYMENT)}{' '}
          down
        </NavigationButton>
        <Text element='span'>
          {' '}
          over 30 years at {formatToPercent(mostRecentInterestRate, 2)}
        </Text>
      </>
    )
  }

  const noDataSubtitle = "Couldn't be determined"

  const loanDataSubtitle = useCallback(() => {
    if (loanPayment === 0 && downPayment >= listingPrice) {
      return 'Home purchased in cash'
    } else if (loanPayment !== 0) {
      return <DownPaymentSubtitle />
    } else {
      return noDataSubtitle
    }
  }, [downPayment, listingPrice, loanPayment])

  const chartData = useMemo(() => {
    const {
      loanPayment,
      hoaFees,
      propertyTaxes,
      mortgageInsurance,
      maintenance,
      homeInsurance,
    } = monthlyCostBreakdown
    const formattedLoan = formatToCurrency(loanPayment)
    const formattedHoa = formatToCurrency(hoaFees)
    const formattedPropTax = formatToCurrency(propertyTaxes)
    const formattedPmi = formatToCurrency(mortgageInsurance)
    const formattedMaintenance = formatToCurrency(maintenance)
    const formattedHomeInsurance = formatToCurrency(homeInsurance)

    const loanData = {
      title: 'Principal & interest',
      subtitle: loanDataSubtitle(),
      value: loanPayment,
      formattedValue: formattedLoan,
    }
    const hoaData = {
      title: 'HOA',
      subtitle: hoaFees ? 'Based on historical figures' : noDataSubtitle,
      value: hoaFees,
      formattedValue: formattedHoa,
    }
    const propTaxData = {
      title: 'Property taxes',
      subtitle: propertyTaxes ? 'Based on historical figures' : noDataSubtitle,
      value: propertyTaxes,
      formattedValue: formattedPropTax,
    }
    const pmiSubtitle =
      mortgageInsurance === undefined
        ? noDataSubtitle
        : mortgageInsurance === 0
        ? 'Not applicable'
        : 'Based on your estimated PMI'
    const mortgageInsuranceData = {
      title: 'Mortgage insurance',
      subtitle: pmiSubtitle,
      value: mortgageInsurance,
      formattedValue: formattedPmi,
    }
    const maintenanceData = {
      title: 'Budget for maintenance',
      subtitle: maintenance ? "Save 1% of home's value / year" : noDataSubtitle,
      value: maintenance,
      formattedValue: formattedMaintenance,
    }
    const homeInsuranceData = {
      title: 'Home insurance',
      subtitle: homeInsurance ? 'Based on national averages' : noDataSubtitle,
      value: homeInsurance,
      formattedValue: formattedHomeInsurance,
    }
    // Sorts high to low with undefined at the bottom
    const sortedByValues = [
      loanData,
      hoaData,
      propTaxData,
      maintenanceData,
      mortgageInsuranceData,
      homeInsuranceData,
    ].sort((a, b) => (a !== undefined && a.value < b.value ? 1 : -1))

    return sortedByValues
  }, [monthlyCostBreakdown, loanDataSubtitle])

  const handleEditYourBudgetClick = () => {
    handleBack()
    setFocusFieldAndNavigateToSettings(INPUT_NAMES.MAX_BUDGET)
  }

  return (
    <OverlayPanel className={styles.overlayPanel}>
      <OverlayPanelHeader onBack={handleBack}>
        <Text weight='semibold' color='white' size='l'>
          Monthly Costs
        </Text>
      </OverlayPanelHeader>
      <Panel className={styles.headerPanel}>
        <Text color='white' weight='light' align='center' size='xxl'>
          <ColoredCountUp
            weight='light'
            value={Math.abs(monthlyCost)}
            controlledColor={budgetColor}
            formatter={formatToCurrency}
          />
        </Text>
        <Text align='center'>
          {`${budgetDirection} budget of ${formatToCurrency(
            Math.abs(maxBudget)
          )} by `}
          <Text color='inherit' element='span' weight='semibold'>
            {formattedBudgetDiff}
          </Text>
        </Text>
      </Panel>
      <Button
        minimal
        iconEnd={BudgetRightArrow}
        onClick={handleEditYourBudgetClick}
        className={styles.editButton}
      >
        Edit your budget
      </Button>
      <div className={styles.headerGroup}>
        <Text upperspaced weight='semibold' letterSpacing='large'>
          Est. Cost Breakdown
        </Text>
        <Button minimal onClick={toggleUnderConstructionActive}>
          Edit
        </Button>
      </div>
      <If condition={chartData}>
        <Panel className={styles.chartPanel}>
          <PanelDivider header className={styles.panelDivider}>
            <HorizontalStackedBarChart.Chart data={chartData} />
          </PanelDivider>

          <HorizontalStackedBarChart.Legend data={chartData} />
        </Panel>
      </If>

      <UnderConstructionModal
        underConstructionActive={underConstructionActive}
        handleModalToggle={toggleUnderConstructionActive}
      />
    </OverlayPanel>
  )
}

MonthlyCostPanel.propTypes = {
  handleBack: PropTypes.func,
}

export default MonthlyCostPanel
